import React, { useRef, useState } from "react";
import SelectDropDown from "@/components/Common/SelectDropDown";
import { PICKER_STYLES } from "@/helpers/pickerStyles";
import { checkDemand, confirmDemandDetails, requestDemandDetails } from "@/services/demandService";
import useTurnstile from "./useTurnstile";
import "./ShowcaseDemandCheck.scss";

const VISITOR_KEY = "gitchaDemandVisitor";

export const visitorId = () => {
  try {
    const saved = window.localStorage.getItem(VISITOR_KEY);
    if (saved) return saved;
    const made = `v${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    window.localStorage.setItem(VISITOR_KEY, made);
    return made;
  } catch {
    return "";
  }
};

export const problemOf = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  if (status === 400 && data?.result?.errors?.address === "noZip") {
    return "We could not find a ZIP code for that address. Add it and check again.";
  }
  if (status === 400 && data?.result?.errors?.address) return "We could not find that address. Add the city and state.";
  return data?.message || "The check did not go through. Check your connection and try again.";
};

const VERIFY_LINE = "Verification didn't load. Reload the page or turn off blockers.";

const FIELD_LINES = {
  name: "Enter your name.",
  email: "Enter an email address you check.",
  phone: "Enter a 10-digit phone number, or leave it empty.",
};

const areaLabel = (area) =>
  area?.city ? `${area.city}${area.state ? `, ${area.state}` : ""} ${area.zip || ""}`.trim() : `ZIP ${area?.zip || ""}`;

export const HowWeCount = () => (
  <p className="demand-check__how">
    We count live Buyer Want&#8209;Listings from agents in this network whose area covers your ZIP code. Every address in
    the same ZIP code gets the same answer. Counts are ranges and never name a buyer or their&nbsp;agent.
  </p>
);

export const DetailsTable = ({ priceRanges, timing }) => (
  <div className="demand-check__details">
    {priceRanges?.length ? (
      <table className="demand-check__table">
        <caption>Price ranges</caption>
        <tbody>
          {priceRanges.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              <td>{row.range}</td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : null}
    {timing?.length ? (
      <table className="demand-check__table">
        <caption>Timing</caption>
        <tbody>
          {timing.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              <td>{row.range}</td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : null}
  </div>
);

const ShowcaseDemandCheck = ({ showcaseKey, brokerageName, host = "", confirmToken = "", onConfirmHandled }) => {
  const [address, setAddress] = useState("");
  const [state, setState] = useState(confirmToken ? "confirm" : "ask");
  const [result, setResult] = useState(null);
  const [details, setDetails] = useState(null);
  const [problem, setProblem] = useState("");
  const [showHow, setShowHow] = useState(false);
  const [values, setValues] = useState({ name: "", email: "", phone: "", agentSlug: null, website: "" });
  const [errors, setErrors] = useState({});
  const [sentTo, setSentTo] = useState("");
  const openedAt = useRef(Date.now());
  const challenge = useTurnstile();
  const office = brokerageName || "This brokerage";

  // Confirm only on a click: mail scanners that open links would otherwise use up the single-use token
  // and confirm an email address on behalf of someone who never asked.
  const confirm = () => {
    if (state !== "confirm") return;
    setState("confirming");
    confirmDemandDetails(confirmToken)
      .then((response) => {
        setDetails(response?.result?.data || null);
        setState("confirmed");
      })
      .catch((error) => {
        setProblem(problemOf(error));
        setState("ask");
      })
      .finally(() => onConfirmHandled?.());
  };

  const check = async (event) => {
    event.preventDefault();
    if (address.trim().length < 5) {
      setProblem("Enter the street address, city and state.");
      return;
    }
    setProblem("");
    setState("checking");
    try {
      const response = await checkDemand(showcaseKey, {
        address: address.trim(),
        visitorId: visitorId(),
        turnstileToken: challenge.token,
      });
      setResult(response?.result?.data || null);
      setState("result");
      openedAt.current = Date.now();
    } catch (error) {
      setProblem(problemOf(error));
      setState("ask");
    } finally {
      challenge.reset();
    }
  };

  const update = (key) => (event) => {
    setValues((current) => ({ ...current, [key]: event.target.value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const send = async (event) => {
    event.preventDefault();
    setProblem("");
    setState("sending");
    try {
      const response = await requestDemandDetails(showcaseKey, {
        ...values,
        zip: result?.area?.zip,
        address: address.trim(),
        host,
        elapsedMs: Date.now() - openedAt.current,
        turnstileToken: challenge.token,
      });
      setSentTo(response?.result?.email || values.email.trim());
      setState("sent");
    } catch (error) {
      const fields = error?.response?.data?.result?.errors || {};
      setErrors(fields);
      setProblem(Object.keys(fields).length ? "" : problemOf(error));
      setState("details");
    } finally {
      challenge.reset();
    }
  };

  const agents = result?.routing?.visitorPicks ? result.routing.agents || [] : [];
  const agentOptions = agents.map((agent) => ({ value: agent.slug, label: agent.name }));

  return (
    <section className="demand-check" aria-labelledby="demand-check-title">
      {state === "confirm" ? (
        <div className="demand-check__result">
          <h2 id="demand-check-title" className="demand-check__title">
            Confirm your email to see the price ranges and&nbsp;timing
          </h2>
          <button type="button" className="demand-check__button" onClick={confirm}>
            Confirm and show me
          </button>
        </div>
      ) : null}

      {state === "confirming" ? (
        <p className="demand-check__status" role="status">
          Confirming your email…
        </p>
      ) : null}

      {state === "confirmed" && details ? (
        <div className="demand-check__result" role="status">
          <h2 id="demand-check-title" className="demand-check__title">
            Buyer Want&#8209;Listings that fit homes in ZIP {details.area?.zip}: <strong>{details.range}</strong>
          </h2>
          <DetailsTable priceRanges={details.priceRanges} timing={details.timing} />
          <p className="demand-check__fine">
            {details.agent?.firstName
              ? `${details.agent.firstName} ${details.agent.lastName || ""} at ${details.brokerage}`.replace(/\s+at/, " at")
              : details.brokerage}{" "}
            will contact you about these buyers. We sent the same details to your&nbsp;email.
          </p>
        </div>
      ) : null}

      {state === "ask" || state === "checking" ? (
        <form className="demand-check__ask" onSubmit={check} noValidate>
          <label id="demand-check-title" className="demand-check__title" htmlFor="demand-check-address">
            Are buyers looking for a home like&nbsp;yours?
          </label>
          <div className="demand-check__line">
            <input
              id="demand-check-address"
              className="demand-check__input"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              placeholder="Your home address"
              autoComplete="street-address"
              maxLength={200}
            />
            <button
              type="submit"
              className="demand-check__button"
              disabled={state === "checking" || !challenge.ready}
            >
              {state === "checking" ? "Checking…" : "Check"}
            </button>
          </div>
          {problem ? (
            <p className="demand-check__problem" role="alert">
              {problem}
            </p>
          ) : null}
          {challenge.error ? <p className="demand-check__problem" role="alert">{VERIFY_LINE}</p> : null}
        </form>
      ) : null}

      {(state === "result" || state === "details" || state === "sending") && result ? (
        <div className="demand-check__result">
          <h2 id="demand-check-title" className="demand-check__title">
            Buyer Want&#8209;Listings that fit homes in {areaLabel(result.area)}: <strong>{result.range}</strong>
          </h2>
          <div className="demand-check__links">
            <button type="button" className="demand-check__link" aria-expanded={showHow} onClick={() => setShowHow(!showHow)}>
              How we count this
            </button>
            <button
              type="button"
              className="demand-check__link"
              onClick={() => {
                setState("ask");
                setResult(null);
              }}
            >
              Check another address
            </button>
          </div>
          {showHow ? <HowWeCount /> : null}
          {state === "result" && result.range !== "0" ? (
            <button type="button" className="demand-check__button" onClick={() => setState("details")}>
              See their price ranges and&nbsp;timing
            </button>
          ) : null}
          {state === "details" || state === "sending" ? (
            <form className="demand-check__form" onSubmit={send} noValidate>
              <div className="demand-check__fields">
                <input
                  className="demand-check__input"
                  value={values.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  autoComplete="name"
                  aria-label="Your name"
                  aria-invalid={Boolean(errors.name)}
                />
                <input
                  className="demand-check__input"
                  type="email"
                  value={values.email}
                  onChange={update("email")}
                  placeholder="Email"
                  autoComplete="email"
                  aria-label="Email"
                  aria-invalid={Boolean(errors.email)}
                />
                <input
                  className="demand-check__input"
                  type="tel"
                  value={values.phone}
                  onChange={update("phone")}
                  placeholder="Phone (optional)"
                  autoComplete="tel"
                  aria-label="Phone (optional)"
                  aria-invalid={Boolean(errors.phone)}
                />
                {agentOptions.length ? (
                  <SelectDropDown
                    id="demand-check-agent"
                    className="demand-check__agent"
                    classNamePrefix="select"
                    placeholder="Agent you would like to hear from"
                    options={agentOptions}
                    value={agentOptions.find((option) => option.value === values.agentSlug) || null}
                    styles={PICKER_STYLES}
                    errorClass="d-none"
                    onChange={(option) => option && setValues((current) => ({ ...current, agentSlug: option.value }))}
                  />
                ) : null}
                <input className="demand-check__trap" tabIndex={-1} autoComplete="off" aria-hidden="true" value={values.website} onChange={update("website")} />
              </div>
              {Object.keys(errors)
                .filter((key) => errors[key] && FIELD_LINES[key])
                .map((key) => (
                  <p key={key} className="demand-check__problem" role="alert">
                    {FIELD_LINES[key]}
                  </p>
                ))}
              {problem ? (
                <p className="demand-check__problem" role="alert">
                  {problem}
                </p>
              ) : null}
              {challenge.error ? <p className="demand-check__problem" role="alert">{VERIFY_LINE}</p> : null}
              <button type="submit" className="demand-check__button" disabled={state === "sending" || !challenge.ready}>
                {state === "sending" ? "Sending…" : "Email me the price ranges and timing"}
              </button>
              <p className="demand-check__fine">
                We&rsquo;ll confirm your email. {office} will contact you about these&nbsp;buyers.
              </p>
            </form>
          ) : null}
        </div>
      ) : null}

      {state === "sent" ? (
        <div className="demand-check__result" role="status">
          <h2 id="demand-check-title" className="demand-check__title">
            Check your email at {sentTo}
          </h2>
          <p className="demand-check__fine">Tap the link in it to see the price ranges and timing. The link works for 24&nbsp;hours.</p>
        </div>
      ) : null}

      <div ref={challenge.ref} className="demand-check__challenge" />
    </section>
  );
};

export default ShowcaseDemandCheck;
